import type { PulseChart } from '../chart.js';
import type { ReplayInputs } from '../replay.js';
import type { Variant } from '../gameTypes.js';
import { VARIANTS, buildVariantSlug } from '../gameTypes.js';
import { buildVariantTitle } from '../chart.js';
import { ChartBuilder, expectedReplayFromPads } from '../engine/pulse/chartBuilder.js';
import { PALETTE_PRISM } from './palettes.js';
import {
  buildBuiltinAudio,
  findBurstChambers,
  mergeBeats,
  variantMergeBeatsWithSpacing,
  variantSelectBeats,
} from './audioFromOnsets.js';
import onsets from './audio/diamond.onsets.json' with { type: 'json' };

const audio = buildBuiltinAudio({
  src: '/audio/tracks/diamond.mp3',
  attribution: '"Diamond" by NIVIRO [NCS Release] - https://ncs.io/Diamond',
  data: onsets,
});

function buildChart(variant: Variant): PulseChart {
  const builder = new ChartBuilder({
    id: buildVariantSlug('diamond', variant),
    slug: buildVariantSlug('diamond', variant),
    title: buildVariantTitle('Diamond', variant),
    description:
      'Future bass at 150 BPM. Bright, sparkly leads carry the rhythm with snare backbeat - a melodic mid-tier with lots of off-beat accents.',
    bpm: onsets.bpm,
    difficulty: 3,
    variant,
    spawnSurface: 'floor',
    palette: PALETTE_PRISM,
    audio,
  });

  // 60s @ 150 BPM ≈ 150 beats. Future bass with a lead-driven melody (only
  // 11 kicks across the cut), so we lean on snares + leads for rhythm.
  builder.section({ startBeat: 0, endBeat: 16, kind: 'verse' });
  builder.section({ startBeat: 16, endBeat: 64, kind: 'drop' });
  builder.section({ startBeat: 64, endBeat: 80, kind: 'bridge' });
  builder.section({ startBeat: 80, endBeat: 150, kind: 'drop' });

  // Build: snare + lead. 1-beat spacing at 150 BPM = 400ms - approachable.
  // Leads are detector-bounded at intensity 0.25 (everything <=0.25 is
  // classified 'lead'); 0.18+ catches the prominent phrase peaks.
  const buildPads = variantSelectBeats(variant, onsets, {
    fromBeat: 4,
    untilBeat: 16,
    kinds: ['snare', 'lead'],
    minIntensity: 0.18,
    minSpacing: 1,
  });

  // First drop: snare backbeat + the song's melodic hook. Diamond is
  // lead-driven (266 leads vs 48 snares), so we pull leads down to 0.18 to
  // catch the riff. Cross-kind merge at 150 BPM uses 0.5-beat floor to
  // avoid a snare+lead 100ms double-tap.
  const drop1 = variantMergeBeatsWithSpacing(
    variant,
    0.5,
    variantSelectBeats(variant, onsets, {
      fromBeat: 16,
      untilBeat: 64,
      kinds: ['snare'],
      minIntensity: 0.2,
      minSpacing: 0.75,
    }),
    variantSelectBeats(variant, onsets, {
      fromBeat: 16,
      untilBeat: 64,
      kinds: ['lead'],
      minIntensity: 0.18,
      minSpacing: 1,
    }),
  );

  // Break: lead-only melodic moment.
  const bridgePads = variantSelectBeats(variant, onsets, {
    fromBeat: 64,
    untilBeat: 80,
    kinds: ['lead'],
    minIntensity: 0.18,
    minSpacing: 1.5,
  });

  // Final drop: same recipe as first drop. The last beats fade out.
  const drop2 = variantMergeBeatsWithSpacing(
    variant,
    0.5,
    variantSelectBeats(variant, onsets, {
      fromBeat: 80,
      untilBeat: 148,
      kinds: ['snare'],
      minIntensity: 0.2,
      minSpacing: 0.75,
    }),
    variantSelectBeats(variant, onsets, {
      fromBeat: 80,
      untilBeat: 148,
      kinds: ['lead'],
      minIntensity: 0.18,
      minSpacing: 1,
    }),
  );

  const padBeats = mergeBeats(buildPads, drop1, bridgePads, drop2);
  for (const beat of padBeats) {
    builder.pad(beat);
  }

  for (const c of findBurstChambers(padBeats, {
    fromBeat: 4,
    untilBeat: 148,
    minGapBeats: 6,
  })) {
    builder.burstChamber(c);
  }

  builder.setTotalBeats(150);
  return builder.build();
}

export const DIAMOND_VARIANTS: Record<Variant, PulseChart> = Object.fromEntries(
  VARIANTS.map((v) => [v, buildChart(v)]),
) as Record<Variant, PulseChart>;

export const DIAMOND_EXPECTED_REPLAYS: Record<Variant, ReplayInputs> =
  Object.fromEntries(
    VARIANTS.map((v) => [v, expectedReplayFromPads(DIAMOND_VARIANTS[v])]),
  ) as Record<Variant, ReplayInputs>;
